import type { IControl } from 'maplibre-gl';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useMap } from '@/features/map';

/** An empty control group for React to render into, placed above the location button. */
class SlotControl implements IControl {
  readonly container = document.createElement('div');

  constructor() {
    this.container.className = 'maplibregl-ctrl maplibregl-ctrl-group';
  }

  onAdd() {
    return this.container;
  }

  /** MapLibre appends top-right controls in the order added; ours goes between compass and location. */
  placeBeforeGeolocate() {
    const geolocate = this.container.parentElement
      ?.querySelector('.maplibregl-ctrl-geolocate')
      ?.closest('.maplibregl-ctrl');
    if (geolocate) geolocate.before(this.container);
  }

  onRemove() {
    this.container.remove();
  }
}

interface Props {
  is3d: boolean;
  onToggle: () => void;
}

/** Map button naming the view a click switches to: "3D" on the flat map, "2D" on the tilted one. */
export function PitchToggle({ is3d, onToggle }: Props) {
  const { t } = useTranslation('terrain');
  const map = useMap();
  // A detached element until added: creating it has no side effects.
  const [slot] = useState(() => new SlotControl());

  useEffect(() => {
    if (!map) return;
    map.addControl(slot, 'top-right');
    slot.placeBeforeGeolocate();
    return () => void map.removeControl(slot);
  }, [map, slot]);

  if (!map) return null;
  return createPortal(
    <button
      type="button"
      className="font-sans text-[13px] font-bold text-neutral-800"
      aria-label={t(is3d ? 'flatten' : 'tilt')}
      title={t(is3d ? 'flatten' : 'tilt')}
      onClick={onToggle}
    >
      {t(is3d ? '2d' : '3d')}
    </button>,
    slot.container,
  );
}
